/**
 * Error toast strip. Shows the tail of `state.errors` (server `error{code,
 * message}` envelopes + locally-detected schema errors). Auto-dismisses after
 * a short timeout, scoped per `at`.
 *
 * Phase 11 (v1.2): each toast slides in from the right edge via `<m.div>` +
 * `<AnimatePresence>`; an explicit "dismiss" `×` button lets the user clear
 * a toast before its TTL expires (local dismissal — the store keeps the
 * error in `state.errors`, this component just hides it). `MotionConfig
 * reducedMotion` in App.tsx snaps the slide off automatically when the OS
 * prefers reduced motion.
 */
import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, m } from "motion/react";
import { useErrors } from "../state/hooks.js";

const TOAST_TTL_MS = 4_000;

const ENTER = { x: 32, opacity: 0 };
const SHOW = { x: 0, opacity: 1 };
const EXIT = { x: 32, opacity: 0 };
const TOAST_TRANSITION = {
  type: "spring" as const,
  stiffness: 360,
  damping: 28,
  mass: 0.5,
};

function keyOf(e: { code: string; at: number }): string {
  return e.at + ":" + e.code;
}

export function ErrorToasts(): JSX.Element | null {
  const errors = useErrors();
  const [now, setNow] = useState(() => Date.now());
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());
  // v1.4.1 Phase 21.6 — gate the TTL-prune interval on `errors.length > 0`.
  // Previously this ran every 1 s for the entire session, even when no
  // toasts were visible — pure idle battery cost on mobile. Now the
  // interval only installs while there's at least one error to prune; the
  // dep on `errors.length` re-installs it the moment a new error arrives
  // and tears it down once the array is empty.
  useEffect(() => {
    if (errors.length === 0) return;
    const id = setInterval(() => setNow(Date.now()), 1_000);
    return () => clearInterval(id);
  }, [errors.length]);

  const visible = useMemo(
    () =>
      errors.filter(
        (e) => now - e.at < TOAST_TTL_MS && !dismissed.has(keyOf(e)),
      ),
    [errors, now, dismissed],
  );

  // Auto-prune expired entries from the dismissed set so it doesn't grow
  // unbounded across a session.
  useEffect(() => {
    if (dismissed.size === 0) return;
    const live = new Set(errors.map(keyOf));
    let changed = false;
    const next = new Set<string>();
    for (const k of dismissed) {
      if (live.has(k)) {
        next.add(k);
      } else {
        changed = true;
      }
    }
    if (changed) setDismissed(next);
  }, [errors, dismissed]);

  if (visible.length === 0 && dismissed.size === 0) return null;
  return (
    <div className="toast-stack" role="region" aria-live="assertive">
      <AnimatePresence initial={false}>
        {visible.map((e) => {
          const k = keyOf(e);
          return (
            <m.div
              key={k}
              className="toast"
              initial={ENTER}
              animate={SHOW}
              exit={EXIT}
              transition={TOAST_TRANSITION}
              layout="position"
            >
              <strong>{e.code}</strong>
              <span>{e.message}</span>
              <button
                type="button"
                className="toast-dismiss"
                aria-label={`Dismiss ${e.code}`}
                onClick={() => {
                  setDismissed((cur) => {
                    const next = new Set(cur);
                    next.add(k);
                    return next;
                  });
                }}
              >
                <span aria-hidden="true">×</span>
              </button>
            </m.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}
